import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Box, Grid, Typography } from '@mui/material';
import { useNotification } from '../../notification/NotificationContext';
import { ApolloError, gql } from '@apollo/client';
import {
  UserUpdatePasswordInput,
  useUpdatePasswordMutation,
} from '../../../../generated/graphql';
import { formikValidation } from '@/common/models/formikValidation';
import { FormTextField } from '../../form/FormTextField';

export const UPDATE_PASSWORD_MUTATION = gql`
  mutation UpdatePassword($data: UserUpdatePasswordInput!) {
    userUpdatePassword(data: $data) {
      _id
    }
  }
`;

export type PasswordUpdateForm = UserUpdatePasswordInput & {
  confirmNewPassword: string;
};

const UserPasswordForm: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [updatePassword, { loading: updatePasswordLoading }] =
    useUpdatePasswordMutation();

  return (
    <>
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        Change Password
      </Typography>

      <Formik
        enableReinitialize
        validateOnBlur
        initialValues={
          {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
          } as PasswordUpdateForm
        }
        validationSchema={Yup.object().shape({
          ...formikValidation.currentPassword,
          ...formikValidation.newPassword,
          ...formikValidation.confirmPassword,
        })}
        onSubmit={async (values, { resetForm }) => {
          try {
            const result = await updatePassword({
              variables: {
                data: {
                  currentPassword: values.currentPassword,
                  newPassword: values.newPassword,
                },
              },
            });
            if (result.data?.userUpdatePassword._id) {
              showSuccess('Password updated successfully');
              resetForm();
            }
          } catch (error) {
            console.error('Password update error:', error);
            const message =
              error instanceof ApolloError
                ? error.message
                : 'Failed to update password. Please try again.';
            showError(message);
          }
        }}
      >
        {({ isSubmitting, dirty, isValid, resetForm }) => (
          <Form>
            <Grid container spacing={1} direction="column">
              <Grid size={{ sm: 12 }}>
                <FormTextField
                  name="currentPassword"
                  label="Current Password"
                  type="password"
                />
              </Grid>

              <Grid size={{ sm: 12 }}>
                <FormTextField
                  name="newPassword"
                  label="New Password"
                  type="password"
                />
              </Grid>

              <Grid size={{ sm: 12 }}>
                <FormTextField
                  name="confirmNewPassword"
                  label="Confirm New Password"
                  type="password"
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                paddingTop: 3,
              }}
            >
              <Button
                type="button"
                variant="outlined"
                onClick={() => resetForm()}
                disabled={!dirty || isSubmitting}
              >
                Reset
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="error"
                disabled={!(isValid && dirty && !isSubmitting)}
                loading={updatePasswordLoading}
              >
                Change Password
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default UserPasswordForm;
